// AUTO-GENERATED — DO NOT EDIT.
// Source of truth: shared/legal/content.ts
// Regenerate: python scripts/sync_legal_content.py

// Canonical legal + company content for TruuCost.
//
// SINGLE SOURCE OF TRUTH for Impressum & Datenschutz across the marketing site
// (Astro) and the app/demo frontend (React). Humans edit ONLY this file.
//
// After editing, regenerate the per-app copies and verify there is no drift:
//   python scripts/sync_legal_content.py
// The quality gate runs `--check` and fails if a generated copy is stale.

export interface Company {
  legalName: string;
  brandNote: string;
  street: string;
  city: string;
  country: string;
  email: string;
  phone: string;
  registerNumber: string;
  registerCourt: string;
  vatId: string;
  chamber: string;
}

export const COMPANY: Company = {
  legalName: "Zackplan GmbH",
  brandNote: "TruuCost ist eine Marke der Zackplan GmbH.",
  street: "Lessinggasse 23/6",
  city: "1020 Wien",
  country: "Österreich",
  email: "office@zackplan.com",
  phone: "+43 660 71 51 690",
  registerNumber: "FN 561867 a",
  registerCourt: "Handelsgericht Wien",
  vatId: "ATU77459634",
  chamber: "Wirtschaftskammer Wien",
};

// Inline text run: a plain string or a hyperlink.
export type Inline =
  | string
  | { kind: "link"; href: string; label: string; external?: boolean };

// Block-level content inside a section.
export type Block =
  | { kind: "paragraph"; content: Inline[] }
  | { kind: "address"; lines: Inline[][] }
  | { kind: "list"; items: Inline[][] };

export interface LegalSection {
  heading?: string;
  blocks: Block[];
}

export interface LegalDoc {
  slug: "impressum" | "datenschutz";
  title: string;
  description: string;
  updated?: string;
  sections: LegalSection[];
}

const mail: Inline = {
  kind: "link",
  href: `mailto:${COMPANY.email}`,
  label: COMPANY.email,
};

export const IMPRESSUM: LegalDoc = {
  slug: "impressum",
  title: "Impressum",
  description:
    "Impressum und Offenlegung der Zackplan GmbH (Marke TruuCost) gemäß § 5 ECG und § 25 Mediengesetz.",
  sections: [
    { blocks: [{ kind: "paragraph", content: [COMPANY.brandNote] }] },
    {
      heading: "Medieninhaber und Anbieter",
      blocks: [
        {
          kind: "address",
          lines: [
            [COMPANY.legalName],
            [COMPANY.street],
            [COMPANY.city],
            [COMPANY.country],
          ],
        },
      ],
    },
    {
      heading: "Kontakt",
      blocks: [
        {
          kind: "address",
          lines: [
            ["E-Mail: ", mail],
            [
              "Telefon: ",
              { kind: "link", href: "tel:+436607151690", label: COMPANY.phone },
            ],
          ],
        },
      ],
    },
    {
      heading: "Unternehmensangaben",
      blocks: [
        {
          kind: "address",
          lines: [
            [`Firmenbuchnummer: ${COMPANY.registerNumber}`],
            [`Firmenbuchgericht: ${COMPANY.registerCourt}`],
            [`UID-Nummer: ${COMPANY.vatId}`],
            [`Mitglied der: ${COMPANY.chamber}`],
          ],
        },
      ],
    },
    {
      heading: "Gewerberecht",
      blocks: [
        {
          kind: "paragraph",
          content: [
            "Es gilt die Gewerbeordnung (GewO) der Republik Österreich, abrufbar unter ",
            {
              kind: "link",
              href: "https://www.ris.bka.gv.at",
              label: "www.ris.bka.gv.at",
              external: true,
            },
            ". Zuständige Aufsichtsbehörde ist der Magistrat der Stadt Wien.",
          ],
        },
      ],
    },
    {
      heading: "Offenlegung gemäß § 25 Mediengesetz",
      blocks: [
        {
          kind: "paragraph",
          content: [
            `Medieninhaber: ${COMPANY.legalName}, ${COMPANY.street}, ${COMPANY.city}, ${COMPANY.country}. Grundlegende Richtung (Blattlinie): Information über das Produkt TruuCost und die Leistungen der Zackplan GmbH.`,
          ],
        },
      ],
    },
    {
      heading: "Haftung für Inhalte",
      blocks: [
        {
          kind: "paragraph",
          content: [
            "Die Inhalte dieser Website wurden mit größtmöglicher Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir gemäß § 16 ff. E-Commerce-Gesetz (ECG) für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich, jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen.",
          ],
        },
      ],
    },
    {
      heading: "Haftung für Links",
      blocks: [
        {
          kind: "paragraph",
          content: [
            "Unser Angebot enthält gegebenenfalls Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Eine Haftung gemäß § 17 ECG ist ausgeschlossen, solange uns keine Rechtswidrigkeit bekannt ist.",
          ],
        },
      ],
    },
    {
      heading: "Urheberrecht",
      blocks: [
        {
          kind: "paragraph",
          content: [
            "Die durch den Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem österreichischen Urheberrecht (UrhG). Beiträge Dritter sind als solche gekennzeichnet. Eine Vervielfältigung, Bearbeitung oder Verbreitung außerhalb der Grenzen des Urheberrechts bedarf der schriftlichen Zustimmung des jeweiligen Urhebers.",
          ],
        },
      ],
    },
  ],
};

export const DATENSCHUTZ: LegalDoc = {
  slug: "datenschutz",
  title: "Datenschutzerklärung",
  description: "Datenschutzerklärung der Zackplan GmbH für die TruuCost-Website.",
  updated: "Juni 2026",
  sections: [
    {
      blocks: [
        {
          kind: "paragraph",
          content: [
            "Der Schutz Ihrer personenbezogenen Daten ist uns wichtig. Wir verarbeiten Ihre Daten ausschließlich auf Grundlage der gesetzlichen Bestimmungen (DSGVO, TKG 2021). In dieser Datenschutzerklärung informieren wir Sie über die wichtigsten Aspekte der Datenverarbeitung im Rahmen dieser Website.",
          ],
        },
      ],
    },
    {
      heading: "Verantwortlicher",
      blocks: [
        {
          kind: "address",
          lines: [
            [COMPANY.legalName],
            [COMPANY.street],
            [COMPANY.city],
            [COMPANY.country],
            ["E-Mail: ", mail],
          ],
        },
      ],
    },
    {
      heading: "Zugriffsdaten / Server-Logfiles",
      blocks: [
        {
          kind: "paragraph",
          content: [
            "Beim Besuch dieser Website erfasst unser Hosting-Anbieter automatisch technische Zugriffsdaten (IP-Adresse, Browsertyp, Betriebssystem sowie Datum und Uhrzeit des Zugriffs). Diese Daten dienen ausschließlich dem technischen Betrieb und der Sicherheit der Website und werden nicht mit anderen Daten zusammengeführt. Rechtsgrundlage ist unser berechtigtes Interesse an einem sicheren und stabilen Betrieb (Art. 6 Abs. 1 lit. f DSGVO).",
          ],
        },
      ],
    },
    {
      heading: "Beta-Anmeldung",
      blocks: [
        {
          kind: "paragraph",
          content: [
            "Wenn Sie sich über das Formular für die Beta anmelden, verarbeiten wir die von Ihnen angegebene E-Mail-Adresse sowie – sofern angegeben – Firma und Notiz. Wir verwenden diese Daten ausschließlich, um mit Ihnen Kontakt rund um die TruuCost-Beta aufzunehmen. Eine Weitergabe an Dritte zu Werbezwecken erfolgt nicht. Rechtsgrundlage ist die Durchführung vorvertraglicher Maßnahmen auf Ihre Anfrage (Art. 6 Abs. 1 lit. b DSGVO) sowie unser berechtigtes Interesse an der Kommunikation mit Interessenten (Art. 6 Abs. 1 lit. f DSGVO).",
          ],
        },
      ],
    },
    {
      heading: "Hosting und Speicherort",
      blocks: [
        {
          kind: "paragraph",
          content: [
            "Diese Website wird auf Servern innerhalb der Europäischen Union gehostet. Es werden keine Daten außerhalb der EU übertragen.",
          ],
        },
      ],
    },
    {
      heading: "Cookies",
      blocks: [
        {
          kind: "paragraph",
          content: [
            "Diese Website verwendet keine Tracking-Cookies und setzt keine Marketing- oder Analyse-Cookies ein. Technisch notwendige Cookies können im Rahmen des Betriebs eingesetzt werden.",
          ],
        },
      ],
    },
    {
      heading: "Speicherdauer",
      blocks: [
        {
          kind: "paragraph",
          content: [
            "Daten aus Ihrer Beta-Anmeldung speichern wir, solange dies für die Bearbeitung Ihrer Anfrage und die Beta-Beziehung erforderlich ist, und löschen sie anschließend bzw. auf Ihren Wunsch. Technische Zugriffsdaten werden spätestens nach 30 Tagen gelöscht.",
          ],
        },
      ],
    },
    {
      heading: "Ihre Rechte",
      blocks: [
        {
          kind: "paragraph",
          content: [
            "Ihnen stehen grundsätzlich die Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch zu (Art. 15–21 DSGVO). Wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer Daten gegen das Datenschutzrecht verstößt, können Sie sich an uns wenden oder bei der zuständigen Aufsichtsbehörde beschweren. In Österreich ist dies die Datenschutzbehörde.",
          ],
        },
      ],
    },
    {
      heading: "Kontakt",
      blocks: [
        {
          kind: "paragraph",
          content: [
            "Bei Fragen zum Datenschutz erreichen Sie uns unter ",
            mail,
            ".",
          ],
        },
      ],
    },
  ],
};

export const LEGAL_DOCS: Record<LegalDoc["slug"], LegalDoc> = {
  impressum: IMPRESSUM,
  datenschutz: DATENSCHUTZ,
};
